import React, { useState } from "react";
import { MockupFrame } from "./MockupFrame";

type Scenario = "conservative" | "moderate" | "aggressive";

const SCENARIOS: Record<Scenario, {
	inputs: Array<{ label: string; value: string; tag?: string }>;
	costs: Array<{ label: string; pct?: string; value: string }>;
	overview: {
		purchase: string; repairs: string; arv: string;
		totalInvested: string;
		targetProfit: string; annTarget: string; monthTarget: string;
		arvFull: string; totalCosts: string;
		projectedProfit: string; annReturn: string; monthReturn: string;
	};
}> = {
	conservative: {
		inputs: [
			{ label: "Purchase Price", value: "$1,000,000" },
			{ label: "Repairs", value: "$15,000", tag: "Finance" },
			{ label: "After Repaired Value (ARV)", value: "$1,150,000" },
			{ label: "Project Length (months)", value: "3.5" },
			{ label: "Loan Interest", value: "8.35%" },
			{ label: "Loan Fee", value: "$1,000" },
			{ label: "Loan Points", value: "0.4", tag: "Finance" },
			{ label: "Transfer Fees", value: "$1,350" },
			{ label: "Closing Cost (Purchase)", value: "0.5%" },
			{ label: "Closing Cost (Selling)", value: "0.5%" },
			{ label: "Property Tax Rate", value: "1.5%" },
			{ label: "Insurance", value: "$200" },
			{ label: "Utilities", value: "$300" },
			{ label: "Staging", value: "$5,000" },
			{ label: "Pictures", value: "$525" },
			{ label: "Cleaning", value: "$600" },
			{ label: "Agent(s) (Purchase)", value: "0%" },
			{ label: "Listing Agent (Selling)", value: "1.75%" },
			{ label: "Buyers Agent (Selling)", value: "2.5%" },
		],
		costs: [
			{ label: "Purchase Price", value: "$1,000,000" },
			{ label: "Repairs", value: "$15,000" },
			{ label: "Loan Interest", pct: "(8.35%)", value: "$24,817" },
			{ label: "Loan Fee", value: "$1,000" },
			{ label: "Loan Points", pct: "(0.4%)", value: "$4,000" },
			{ label: "Transfer Fees", value: "$1,350" },
			{ label: "Closing Cost (Purchase)", pct: "(0.5%)", value: "$5,000" },
			{ label: "Closing Cost (Selling)", pct: "(0.5%)", value: "$5,750" },
			{ label: "Property Tax Rate", pct: "(1.5%)", value: "$4,375" },
			{ label: "Insurance", pct: "($200/mo)", value: "$700" },
			{ label: "Utilities", pct: "($300/mo)", value: "$1,050" },
			{ label: "Staging", value: "$5,000" },
			{ label: "Pictures", value: "$525" },
			{ label: "Cleaning", value: "$600" },
			{ label: "Agent(s) (Purchase)", pct: "(0%)", value: "$0" },
			{ label: "Listing Agent (Selling)", pct: "(1.75%)", value: "$20,125" },
			{ label: "Buyers Agent (Selling)", pct: "(2.5%)", value: "$28,750" },
		],
		overview: {
			purchase: "$1M", repairs: "$15K", arv: "$1.15M",
			totalInvested: "$1,063,417",
			targetProfit: "$108,557", annTarget: "35%", monthTarget: "10.21%",
			arvFull: "$1,150,000", totalCosts: "- $1,118,042",
			projectedProfit: "$31,958", annReturn: "10.30%", monthReturn: "3.01%",
		},
	},
	moderate: {
		inputs: [
			{ label: "Purchase Price", value: "$850,000" },
			{ label: "Repairs", value: "$45,000", tag: "Finance" },
			{ label: "After Repaired Value (ARV)", value: "$1,050,000" },
			{ label: "Project Length (months)", value: "4" },
			{ label: "Loan Interest", value: "7.5%" },
			{ label: "Loan Fee", value: "$1,200" },
			{ label: "Loan Points", value: "0.5", tag: "Finance" },
			{ label: "Transfer Fees", value: "$1,200" },
			{ label: "Closing Cost (Purchase)", value: "0.5%" },
			{ label: "Closing Cost (Selling)", value: "0.5%" },
			{ label: "Property Tax Rate", value: "1.25%" },
			{ label: "Insurance", value: "$180" },
			{ label: "Utilities", value: "$250" },
			{ label: "Staging", value: "$4,000" },
			{ label: "Pictures", value: "$500" },
			{ label: "Cleaning", value: "$500" },
			{ label: "Agent(s) (Purchase)", value: "0%" },
			{ label: "Listing Agent (Selling)", value: "2%" },
			{ label: "Buyers Agent (Selling)", value: "2.5%" },
		],
		costs: [
			{ label: "Purchase Price", value: "$850,000" },
			{ label: "Repairs", value: "$45,000" },
			{ label: "Loan Interest", pct: "(7.5%)", value: "$21,250" },
			{ label: "Loan Fee", value: "$1,200" },
			{ label: "Loan Points", pct: "(0.5%)", value: "$4,250" },
			{ label: "Transfer Fees", value: "$1,200" },
			{ label: "Closing Cost (Purchase)", pct: "(0.5%)", value: "$4,250" },
			{ label: "Closing Cost (Selling)", pct: "(0.5%)", value: "$5,250" },
			{ label: "Property Tax Rate", pct: "(1.25%)", value: "$3,542" },
			{ label: "Insurance", pct: "($180/mo)", value: "$720" },
			{ label: "Utilities", pct: "($250/mo)", value: "$1,000" },
			{ label: "Staging", value: "$4,000" },
			{ label: "Pictures", value: "$500" },
			{ label: "Cleaning", value: "$500" },
			{ label: "Agent(s) (Purchase)", pct: "(0%)", value: "$0" },
			{ label: "Listing Agent (Selling)", pct: "(2%)", value: "$21,000" },
			{ label: "Buyers Agent (Selling)", pct: "(2.5%)", value: "$26,250" },
		],
		overview: {
			purchase: "$850K", repairs: "$45K", arv: "$1.05M",
			totalInvested: "$944,912",
			targetProfit: "$94,500", annTarget: "30%", monthTarget: "9.12%",
			arvFull: "$1,050,000", totalCosts: "- $989,912",
			projectedProfit: "$60,088", annReturn: "19.07%", monthReturn: "6.36%",
		},
	},
	aggressive: {
		inputs: [
			{ label: "Purchase Price", value: "$620,000" },
			{ label: "Repairs", value: "$95,000", tag: "Finance" },
			{ label: "After Repaired Value (ARV)", value: "$920,000" },
			{ label: "Project Length (months)", value: "5" },
			{ label: "Loan Interest", value: "9.0%" },
			{ label: "Loan Fee", value: "$1,500" },
			{ label: "Loan Points", value: "0.75", tag: "Finance" },
			{ label: "Transfer Fees", value: "$1,100" },
			{ label: "Closing Cost (Purchase)", value: "0.5%" },
			{ label: "Closing Cost (Selling)", value: "0.5%" },
			{ label: "Property Tax Rate", value: "1.1%" },
			{ label: "Insurance", value: "$160" },
			{ label: "Utilities", value: "$200" },
			{ label: "Staging", value: "$3,500" },
			{ label: "Pictures", value: "$450" },
			{ label: "Cleaning", value: "$400" },
			{ label: "Agent(s) (Purchase)", value: "0%" },
			{ label: "Listing Agent (Selling)", value: "2%" },
			{ label: "Buyers Agent (Selling)", value: "2.5%" },
		],
		costs: [
			{ label: "Purchase Price", value: "$620,000" },
			{ label: "Repairs", value: "$95,000" },
			{ label: "Loan Interest", pct: "(9.0%)", value: "$26,775" },
			{ label: "Loan Fee", value: "$1,500" },
			{ label: "Loan Points", pct: "(0.75%)", value: "$4,650" },
			{ label: "Transfer Fees", value: "$1,100" },
			{ label: "Closing Cost (Purchase)", pct: "(0.5%)", value: "$3,100" },
			{ label: "Closing Cost (Selling)", pct: "(0.5%)", value: "$4,600" },
			{ label: "Property Tax Rate", pct: "(1.1%)", value: "$2,842" },
			{ label: "Insurance", pct: "($160/mo)", value: "$800" },
			{ label: "Utilities", pct: "($200/mo)", value: "$1,000" },
			{ label: "Staging", value: "$3,500" },
			{ label: "Pictures", value: "$450" },
			{ label: "Cleaning", value: "$400" },
			{ label: "Agent(s) (Purchase)", pct: "(0%)", value: "$0" },
			{ label: "Listing Agent (Selling)", pct: "(2%)", value: "$18,400" },
			{ label: "Buyers Agent (Selling)", pct: "(2.5%)", value: "$23,000" },
		],
		overview: {
			purchase: "$620K", repairs: "$95K", arv: "$920K",
			totalInvested: "$762,117",
			targetProfit: "$138,000", annTarget: "40%", monthTarget: "14.52%",
			arvFull: "$920,000", totalCosts: "- $807,117",
			projectedProfit: "$112,883", annReturn: "35.49%", monthReturn: "14.81%",
		},
	},
};

export function MockupCalculator() {
	const [activeScenario, setActiveScenario] = useState<Scenario>("conservative");
	const [highlightedRow, setHighlightedRow] = useState<number | null>(null);
	const [projectionMode, setProjectionMode] = useState(true);

	const data = SCENARIOS[activeScenario];
	const ov = data.overview;

	return (
		<MockupFrame url="comps.red | ROI Calculator">
			<div className="flex flex-col">
				{/* Calculator Header Bar */}
				<div className="flex items-center justify-between px-4 py-2 border-b border-white/5 bg-zinc-900/80">
					<div className="flex items-center gap-3">
						<div className="flex items-center gap-1.5">
							<div className="h-4 w-4 rounded bg-[#db241e] flex items-center justify-center">
								<span className="text-[7px] font-bold text-white">C</span>
							</div>
							<span className="text-[10px] font-bold text-white">COMPS.red</span>
						</div>
						<span className="text-[10px] text-zinc-500">ROI Calculator</span>
						<span className="text-[10px] text-zinc-400">17395 De Witt Ave, Morgan Hill, CA 95037</span>
					</div>
					<div className="flex items-center gap-2">
						<div className="flex items-center gap-1.5">
							{(["conservative", "moderate", "aggressive"] as Scenario[]).map((s) => (
								<button
									key={s}
									onClick={() => setActiveScenario(s)}
									className={`rounded px-2 py-0.5 text-[9px] font-medium capitalize transition-all ${
										activeScenario === s
											? "bg-zinc-700 text-white"
											: "text-zinc-500 hover:text-zinc-400"
									}`}
								>
									{s === "conservative" ? "Bob's flooring" : s === "moderate" ? "Kitchen reno" : "Full gut"}
								</button>
							))}
						</div>
						<button
							onClick={() => setProjectionMode(!projectionMode)}
							className={`rounded px-2 py-0.5 text-[9px] font-medium flex items-center gap-1 transition-all ${
								projectionMode ? "bg-emerald-500/20 text-emerald-400" : "text-zinc-500"
							}`}
						>
							<svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
							</svg>
							Projections
						</button>
					</div>
				</div>

				{/* 3-Column Layout */}
				<div className="grid grid-cols-3 gap-0 divide-x divide-white/5">
					{/* Column 1: Project Inputs */}
					<div className="p-4">
						<h3 className="text-sm font-semibold text-white mb-3">Project Inputs</h3>
						<div className="space-y-0">
							{data.inputs.map((row, i) => (
								<div
									key={row.label}
									onMouseEnter={() => setHighlightedRow(i)}
									onMouseLeave={() => setHighlightedRow(null)}
									className={`flex items-center justify-between py-[5px] px-1 rounded transition-colors ${
										highlightedRow === i ? "bg-white/[0.03]" : ""
									}`}
								>
									<div className="flex items-center gap-1.5">
										<span className="text-[10px] text-zinc-400">{row.label}</span>
										{row.tag && (
											<span className="text-[7px] bg-blue-500/20 text-blue-400 px-1 py-0.5 rounded font-medium">
												{row.tag}
											</span>
										)}
									</div>
									<span className="text-[10px] font-medium text-white">{row.value}</span>
								</div>
							))}
						</div>
					</div>

					{/* Column 2: Total Costs Breakdown */}
					<div className="p-4">
						<h3 className="text-sm font-semibold text-white mb-3">Total Costs Breakdown</h3>
						<div className="space-y-0">
							{data.costs.map((row, i) => (
								<div
									key={i}
									onMouseEnter={() => setHighlightedRow(i)}
									onMouseLeave={() => setHighlightedRow(null)}
									className={`flex items-center justify-between py-[5px] px-1 rounded transition-colors ${
										highlightedRow === i ? "bg-white/[0.03]" : ""
									}`}
								>
									<div className="flex items-center gap-1.5">
										<span className="text-[10px] text-zinc-400">{row.label}</span>
										{row.pct && <span className="text-[8px] text-zinc-600">{row.pct}</span>}
									</div>
									<span className="text-[10px] font-medium text-white">{row.value}</span>
								</div>
							))}
						</div>
					</div>

					{/* Column 3: Deal Overview */}
					<div className="p-4">
						<h3 className="text-sm font-semibold text-white mb-3">Deal Overview</h3>

						{/* Purchase + Repairs → ARV */}
						<div className="flex items-center gap-1.5 mb-4">
							<div className="flex-1 rounded-lg border border-white/10 bg-zinc-800/40 p-2 text-center">
								<p className="text-[8px] text-zinc-500">Purchase</p>
								<p className="text-xs font-bold text-white">{ov.purchase}</p>
							</div>
							<span className="text-[10px] text-zinc-600">+</span>
							<div className="flex-1 rounded-lg border border-white/10 bg-zinc-800/40 p-2 text-center">
								<p className="text-[8px] text-zinc-500">Repairs</p>
								<p className="text-xs font-bold text-white">{ov.repairs}</p>
							</div>
							<span className="text-[10px] text-zinc-600">=</span>
							<div className="flex-1 rounded-lg border border-white/10 bg-zinc-800/40 p-2 text-center">
								<p className="text-[8px] text-zinc-500">ARV</p>
								<p className="text-xs font-bold text-emerald-500">{ov.arv}</p>
							</div>
						</div>

						{/* Total Invested Capital */}
						<div className="rounded-lg border border-white/5 bg-zinc-800/20 p-3 mb-3">
							<p className="text-[9px] text-zinc-500 mb-0.5">Total Invested Capital</p>
							<p className="text-xl font-bold text-white">{ov.totalInvested}</p>
							<p className="text-[8px] text-zinc-600 mt-0.5">Excludes sell-side agent commissions and closing costs</p>
						</div>

						{/* Target Profit */}
						<div className="space-y-1.5 mb-3">
							<div className="flex items-center justify-between">
								<span className="text-[10px] text-zinc-400">Target Profit</span>
								<span className="text-sm font-bold text-emerald-400">{ov.targetProfit}</span>
							</div>
							<div className="flex items-center justify-between">
								<span className="text-[10px] text-zinc-400">Annualized Target Return</span>
								<span className="text-[10px] font-bold text-amber-500 bg-zinc-800 rounded px-1.5 py-0.5">{ov.annTarget}</span>
							</div>
							<div className="flex items-center justify-between">
								<span className="text-[10px] text-zinc-400">
									{activeScenario === "conservative" ? "4" : activeScenario === "moderate" ? "4" : "5"}-Month Target
								</span>
								<span className="text-[10px] font-medium text-zinc-300">{ov.monthTarget}</span>
							</div>
						</div>

						{/* Profit Analysis */}
						<div className="border-t border-white/5 pt-3 mb-3">
							<p className="text-[10px] font-semibold text-zinc-400 mb-2">Profit Analysis</p>
							<div className="space-y-1.5">
								<div className="flex items-center justify-between">
									<span className="text-[10px] text-zinc-400">ARV</span>
									<span className="text-[10px] font-medium text-white">{ov.arvFull}</span>
								</div>
								<div className="flex items-center justify-between">
									<span className="text-[10px] text-zinc-400">Total Costs</span>
									<span className="text-[10px] font-medium text-white">{ov.totalCosts}</span>
								</div>
							</div>
						</div>

						{/* Projected Profit */}
						<div className="flex items-center justify-between mb-2">
							<div className="flex items-center gap-1.5">
								<span className="text-[10px] font-bold text-white">Projected Profit</span>
								<svg width="16" height="8" viewBox="0 0 16 8" className="text-emerald-400">
									<path d="M1 6 Q4 1 8 4 Q12 7 15 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
								</svg>
							</div>
							<span
								className="text-sm font-bold text-emerald-400"
								style={{ filter: "drop-shadow(0 0 6px rgba(52,211,153,0.3))" }}
							>
								{ov.projectedProfit}
							</span>
						</div>
						<div className="space-y-1">
							<div className="flex items-center justify-between">
								<span className="text-[10px] text-zinc-400">Annualized Return</span>
								<span className="text-[10px] font-bold text-emerald-400">{ov.annReturn}</span>
							</div>
							<div className="flex items-center justify-between">
								<span className="text-[10px] text-zinc-400">
									{activeScenario === "conservative" ? "4" : activeScenario === "moderate" ? "4" : "5"}-Month Return
								</span>
								<span className="text-[10px] font-medium text-red-400">{ov.monthReturn}</span>
							</div>
						</div>

						{/* Projection Notes */}
						<div className="border-t border-white/5 mt-3 pt-2">
							<p className="text-[9px] text-zinc-600">Projection Notes</p>
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="border-t border-white/5 px-4 py-1.5 text-center">
					<span className="text-[9px] text-zinc-600">COMPS.red by <span className="text-[#db241e]">LUX.red</span></span>
				</div>
			</div>
		</MockupFrame>
	);
}
